"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.integrateCdnHost = exports.cellValueToImageSrc = exports.getHostOfAttachment = exports.getImageThumbSrc = exports.isSvg = exports.isImage = exports.isWebp = exports.isGif = exports.isPdf = exports.CutMethod = void 0;
const attr_accept_1 = __importDefault(require("attr-accept"));
const config_1 = require("../config");
const urlcat_1 = __importDefault(require("urlcat"));
var CutMethod;
(function (CutMethod) {
    CutMethod["UNCUT"] = "0";
    CutMethod["CUT"] = "1";
})(CutMethod = exports.CutMethod || (exports.CutMethod = {}));
function isPdf(file) {
    return (0, attr_accept_1.default)(file, 'application/pdf');
}
exports.isPdf = isPdf;
function isGif(file) {
    return (0, attr_accept_1.default)(file, 'image/gif');
}
exports.isGif = isGif;
function isWebp(file) {
    return (0, attr_accept_1.default)(file, 'image/webp');
}
exports.isWebp = isWebp;
function isImage(file) {
    return (0, attr_accept_1.default)(file, 'image/*');
}
exports.isImage = isImage;
function isSvg(file) {
    return (0, attr_accept_1.default)(file, 'image/svg+xml');
}
exports.isSvg = isSvg;
function getFileMethod(option) {
    if (!option.method)
        return '/0';
    return `/${option.method}`;
}
function getFileSize(option) {
    const w = option.w ? option.w : option.size;
    const h = option.h ? option.h : option.size;
    //NOTE: w and h must be integer
    if (w && h) {
        return `/w/${Math.floor(w)}/h/${Math.floor(h)}`;
    }
    if (w) {
        return `/w/${Math.floor(w)}`;
    }
    if (h) {
        return `/h/${Math.floor(h)}`;
    }
    return '';
}
function getFileQuality(option) {
    if (option && option.quality) {
        return `/q/${option.quality}!`;
    }
    return '';
}
function getFileFormat(options) {
    if (options && options.format === 'jpg') {
        return '/format/jpg';
    }
    return '';
}
function getImageThumbSrcForQiniu(src, options) {
    if (!/^http/.test(src)) {
        src[0] !== '/' && (src = '/' + src);
    }
    return [
        src,
        '?imageView2',
        getFileMethod(options),
        getFileSize(options),
        getFileFormat(options),
        getFileQuality(options),
    ].join('');
}
function getImageThumbSrc(src, options) {
    var _a;
    if (!options || ((_a = (0, config_1.getCustomConfig)()) === null || _a === void 0 ? void 0 : _a.DISABLED_QINIU_COMPRESSION_PARAMS)) {
        return src;
    }
    return getImageThumbSrcForQiniu(src, options);
}
exports.getImageThumbSrc = getImageThumbSrc;
const getHostOfAttachment = (bucket) => {
    var _a, _b, _c;
    if (typeof window != 'object') {
        return process.env[bucket.toUpperCase()] || process.env.OSS_HOST;
    }
    const origin = window.location.origin;
    if (bucket.toUpperCase() === 'QNY1') {
        const QNY1 = (_a = window.__initialization_data__) === null || _a === void 0 ? void 0 : _a.envVars.QNY1;
        return QNY1.includes('http') ? QNY1 : (0, urlcat_1.default)(origin, QNY1 + '');
    }
    if (bucket.toUpperCase() === 'QNY2') {
        const QNY2 = (_b = window.__initialization_data__) === null || _b === void 0 ? void 0 : _b.envVars.QNY2;
        return QNY2.includes('http') ? QNY2 : (0, urlcat_1.default)(origin, QNY2 + '');
    }
    const QNY3 = (_c = window.__initialization_data__) === null || _c === void 0 ? void 0 : _c.envVars.QNY3;
    return QNY3.includes('http') ? QNY3 : (0, urlcat_1.default)(origin, QNY3 + '');
};
exports.getHostOfAttachment = getHostOfAttachment;
function cellValueToImageSrc(cellValue, options) {
    if (!cellValue)
        return '';
    const { bucket, token, preview: previewToken, mimeType, name } = cellValue;
    const host = (0, exports.getHostOfAttachment)(bucket);
    if (!host)
        return '';
    const { formatToJPG, isPreview } = options || {};
    const fileArgument = { name, type: mimeType };
    const originSrc = (0, urlcat_1.default)(host, token);
    const defaultSrc = getImageThumbSrc(originSrc, options);
    if (isPdf(fileArgument)) {
        if (isPreview && options && Object.keys(options).length > 1) {
            return getImageThumbSrc((0, urlcat_1.default)(host, previewToken), options);
        }
        return originSrc;
    }
    if (isGif(fileArgument) || isWebp(fileArgument)) {
        // The caller is from Swiper and does not use slice parameters
        if (options == null) {
            return originSrc;
        }
        return getImageThumbSrc(originSrc, Object.assign(Object.assign({}, options), { format: formatToJPG ? 'jpg' : undefined }));
    }
    if (isSvg(fileArgument)) {
        return originSrc;
    }
    return defaultSrc;
}
exports.cellValueToImageSrc = cellValueToImageSrc;
const integrateCdnHost = (pathName) => {
    if (!pathName) {
        return pathName;
    }
    const host = (0, exports.getHostOfAttachment)('QNY1');
    // TODO: delete this. Compatible with old version data
    if (pathName.startsWith('http')) {
        return pathName;
    }
    return (0, urlcat_1.default)(host, pathName);
};
exports.integrateCdnHost = integrateCdnHost;
//# sourceMappingURL=file.js.map